// Initialize environment before any other imports
import "./lib/init"
import { RouteError, RoutePending } from "@repo/app-shell"
import { EnvProvider } from "@repo/env/provider"
import * as Sentry from "@sentry/react"
import { QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import { StrictMode } from "react"
import ReactDOM from "react-dom/client"

import { queryClient } from "./lib/convex"
import { clientEnv } from "./lib/init"
import { ConvexProvider } from "./providers/ConvexProvider"
import { StatsigProvider } from "./providers/StatsigProvider"

// import '@fontsource-variable/inter'
import "./index.css"
// Import the generated route tree
import { routeTree } from "./routeTree.gen"

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {},
  defaultErrorComponent: RouteError,
  defaultPendingComponent: RoutePending,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 30_000,
  defaultGcTime: 5 * 60_000,
})

// Initialize Sentry after router is created
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById("root")!
if (!rootElement.innerHTML) {
  console.log("Rendering app")
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <Sentry.ErrorBoundary>
        <ConvexProvider>
          <QueryClientProvider client={queryClient}>
            <StatsigProvider>
              <EnvProvider env={clientEnv}>
                <RouterProvider router={router} />
              </EnvProvider>
            </StatsigProvider>
          </QueryClientProvider>
        </ConvexProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>,
  )
}
